import { useEffect } from 'react';

import { isAddress } from 'viem';
import type { UseQueryOptions } from '@soundxyz/graphql-react-query/reactQuery';
import { useQuery } from '@soundxyz/graphql-react-query/reactQuery';
import type { SAM } from '@soundxyz/sdk';
import { samv1Abi } from '@soundxyz/sdk/abi/sam-v1';
import { MINT_GAS_LIMIT_MULTIPLIER } from '@soundxyz/sdk/utils/constants';
import { scaleAmount } from '@soundxyz/sdk/utils/helpers';
import type { Maybe } from '@soundxyz/utils';
import { assertAddress } from '@soundxyz/utils/address';
import { NULL_ADDRESS, SDK_SOUND_ATTRIBUTON_ID } from '@soundxyz/utils/const';
import { useLatestRef } from '@components-shared/src/hooks/useLatestRef';
import { useAuth } from '@context/auth/base';
import { useConnectedAuth } from '@context/auth/connected';
import { SubscriptionEffects } from '@graphql/graphql-ws';
import { GraphQLReactQuery, ReleaseCollectorUpdateDocument } from '@graphql/reactQuery';
import { useSoundSdk } from '@hooks/useSoundSdk';
import { expectNormalGas, SDK_KEY } from './helpers';

export function useEstimateSamBuyGas({
  editionAddress,
  chainId,
  quantity,
  maxValue,
}: {
  editionAddress: string;
  chainId: number;
  quantity: number;
  maxValue: bigint;
}) {
  const { authUserAddress } = useAuth();
  const { provider } = useConnectedAuth();

  const { soundClientProvider } = useSoundSdk();

  return useQuery({
    staleTime: Infinity,
    cacheTime: Infinity,
    queryKey: [
      SDK_KEY,
      'sam-buy-estimate',
      editionAddress,
      quantity,
      authUserAddress,
      chainId,
      maxValue.toString(),
    ],
    async queryFn() {
      if (!authUserAddress) return null;

      try {
        const clientProvider = soundClientProvider(chainId);
        const estimateProvider = provider(chainId);

        assertAddress(editionAddress);
        const samAddress = await clientProvider.edition.sam({
          editionAddress,
        }).contract.address;

        if (!samAddress || !isAddress(samAddress)) return null;

        assertAddress(editionAddress);

        const gasEstimate = await estimateProvider.estimateContractGas({
          abi: samv1Abi,
          account: authUserAddress,
          address: samAddress,
          functionName: 'buy',
          args: [
            editionAddress,
            authUserAddress,
            quantity,
            NULL_ADDRESS,
            [] satisfies [],
            BigInt(SDK_SOUND_ATTRIBUTON_ID),
          ],
          value: maxValue,
        });

        return {
          gasLimit: expectNormalGas({
            estimation: scaleAmount({ amount: gasEstimate, multiplier: MINT_GAS_LIMIT_MULTIPLIER }),
            editionAddress,
            chainId,
            quantity,
          }),
        };
      } catch (err) {
        // Here we can't report to sentry because insufficient funds which is normal, will throw and add too much noise
        return {
          gasLimit: undefined,
        };
      }
    },
    enabled: !!authUserAddress && !!quantity,
  });
}

export function useEstimateSamSell({
  editionAddress,
  chainId,
  quantity,
  minimumPayout,
  canSellEditions,
}: {
  editionAddress: string;
  chainId: number;
  quantity: number;
  minimumPayout: bigint;
  canSellEditions: boolean;
}) {
  const { authUserAddress } = useAuth();
  const { provider } = useConnectedAuth();
  const { soundClientProvider } = useSoundSdk();

  return useQuery({
    // We do need to refetch since token ids to be sold can change
    staleTime: 0,
    cacheTime: Infinity,
    queryKey: [
      SDK_KEY,
      'sam-sell-estimate',
      editionAddress,
      quantity,
      authUserAddress,
      chainId,
      minimumPayout.toString(),
    ],
    async queryFn() {
      if (!authUserAddress) return null;

      const clientProvider = soundClientProvider(chainId);
      const estimateProvider = provider(chainId);

      assertAddress(editionAddress);
      const sam = clientProvider.edition.sam({
        editionAddress,
      });

      const [tokensToSell, samAddress] = await Promise.all([
        sam.api.availableTokensToSell({
          quantity,
          ownerPublicAddress: authUserAddress,
        }),
        sam.contract.address,
      ]);

      if (!samAddress || !isAddress(samAddress)) return null;

      assertAddress(editionAddress);

      const tokenIds = tokensToSell.map(id => BigInt(id));

      const tokenIdsContract = tokenIds.sort((a, b) => (a > b ? 1 : -1));

      try {
        const gasEstimate = await estimateProvider.estimateContractGas({
          abi: samv1Abi,
          account: authUserAddress,
          address: samAddress,
          functionName: 'sell',
          args: [
            editionAddress,
            tokenIdsContract,
            minimumPayout,
            authUserAddress,
            BigInt(SDK_SOUND_ATTRIBUTON_ID),
          ],
        });

        return {
          gasEstimate: expectNormalGas({
            estimation: scaleAmount({ amount: gasEstimate, multiplier: MINT_GAS_LIMIT_MULTIPLIER }),
            editionAddress,
            chainId,
            quantity,
          }),
          tokenIds: tokenIdsContract,
        };
      } catch (err) {
        // Here we can't report to sentry because insufficient funds which is normal, will throw and add too much noise
        return {
          gasEstimate: undefined,
          tokenIds: tokenIdsContract,
        };
      }
    },
    enabled: canSellEditions && !!authUserAddress && !!quantity,
  });
}

// NOTE: This may not return the data you expect for artist or other contract types
// You probably want to use useGetNumReleaseEditionsOwned instead
export function useNumberOfTokensOwned<QueryData = number>(
  {
    editionAddress,
    chainId,
  }: {
    editionAddress: Maybe<string>;
    chainId: number;
  },
  options?: Omit<
    UseQueryOptions<number, Error, QueryData, (string | undefined)[]>,
    'queryKey' | 'queryFn'
  >,
) {
  const { authUserAddress } = useAuth();
  const { soundClientProvider } = useSoundSdk();

  const queryKey = [
    SDK_KEY,
    'number-of-tokens-owned',
    editionAddress ?? undefined,
    authUserAddress,
    chainId.toString(),
  ];

  const latestQueryKey = useLatestRef(queryKey);

  const { data, refetch } = useQuery({
    queryKey,
    async queryFn() {
      if (!authUserAddress || !editionAddress) return 0;
      const soundProvider = soundClientProvider(chainId);

      assertAddress(editionAddress);
      return soundProvider.edition.numberOfTokensOwned({
        editionAddress,
        userAddress: authUserAddress,
      });
    },
    ...options,
  });

  useEffect(() => {
    if (!authUserAddress) return;

    return SubscriptionEffects.onCompleted(
      ReleaseCollectorUpdateDocument,
      ({
        result: {
          data: { releaseCollectorUpdates },
        },
      }) => {
        if (releaseCollectorUpdates.userPublicAddress === authUserAddress) {
          // Optimistically set the query data but check on contract anyways
          GraphQLReactQuery.setQueryData(
            latestQueryKey.current,
            // When releaseCollectorUpdates.collector is null, it means the user stopped being a collector, so 0 nfts count is valid
            releaseCollectorUpdates.collector?.nftsCount ?? 0,
          );

          refetch();
        }
      },
    );
  }, [authUserAddress, refetch, latestQueryKey]);

  return {
    data,
  };
}

export function useSamInfo<QueryData = SAM>(
  {
    editionAddress,
    chainId,
  }: {
    editionAddress: string;
    chainId: number;
  },
  options?: Omit<UseQueryOptions<SAM | null, Error, QueryData, string[]>, 'queryKey' | 'queryFn'>,
) {
  const { soundClientProvider } = useSoundSdk();

  return useQuery({
    queryKey: [SDK_KEY, 'sam-info', editionAddress],
    queryFn() {
      assertAddress(editionAddress);
      return soundClientProvider(chainId).edition.sam({
        editionAddress,
      }).contract.info;
    },
    // This info almost never changes after edition creation, no need to revalidate
    cacheTime: Infinity,
    staleTime: Infinity,
    ...options,
  });
}
